// Worker profile takeover for the schedule-review side section. Rendered as a
// SideShell.Panel (header = worker name + built-in back button) over the
// Review/Team/Activity tabs when a worker name is clicked in the Team tab.
// Reproduces the PR #6802 prototype profile: Profile / Preferences / Upcoming
// Changes, with an in-session FTE bar. Mock-backed today; swap for GraphQL once
// the load-balancing worker fields land.
import React from 'react';
import {
  AlignedInfo,
  Button,
  Divider,
  ErrorHandler,
  Gap,
  ProgressBar,
  SideShell,
  StatusPill,
  Tag,
  Text,
} from '@common/components';
import { useQuery } from '@common/hooks';
import { todayIso } from '@common/services/datetime';
import type { ScheduleTeamMemberType } from '@load-balancing/types';
import { ScheduleWorkerUpcomingChanges } from '@load-balancing/components';
import { LOAD_BALANCING_WORKER_QUERY } from '@load-balancing/graphql/queries/load-balancing-worker';
import { toTeamReviewWorker } from '@load-balancing/services';
import {
  getChargeEligibleLabel,
  getEmploymentLabel,
  getFteBarColor,
  getFteRemainingLabel,
  getShiftPatternLabel,
  getShiftTypeLabel,
} from './_schedule-worker-profile-panel-service';

export function ScheduleWorkerProfilePanel({
  member,
  chargeRole,
  schedulePeriodId,
  onUpdateProfile,
}: {
  member: ScheduleTeamMemberType;
  // The run's configured charge-role abbreviation (data-driven): charge eligibility is
  // meaningful only for non-charge roles, so the label reads "—" when the worker IS the charge role.
  chargeRole?: string;
  // The reviewed run's scheduling period: drives period-based FTE hours on each upcoming change.
  schedulePeriodId?: string | null;
  // Opens the Update Profile form: with no argument to create, or with a term id to edit
  // that scheduled change.
  onUpdateProfile: (changeId?: string) => void;
}) {
  // Pull the worker's term history for the Upcoming Changes section (the review-grid member
  // record carries only current-term fields). Anchored on today, like the drawer.
  const { data, error, loading } = useQuery(LOAD_BALANCING_WORKER_QUERY, {
    variables: { id: member.id, schedulingPeriodId: schedulePeriodId },
    skip: !member.id,
  });

  if (error) {
    return <ErrorHandler error={error} log="ScheduleWorkerProfilePanel" />;
  }

  const upcomingChanges = data?.loadBalancingWorker
    ? toTeamReviewWorker(data.loadBalancingWorker, todayIso()).upcomingChanges
    : [];

  const profileContent: React.ReactNode[][] = [
    [
      'Status',
      <Gap key="status" size="xs" align="center">
        <StatusPill variant={member.active ? 'success' : 'neutral'}>
          {member.active ? 'Active' : 'Inactive'}
        </StatusPill>
        <Tag kind="square" color="steelLighter">
          {getEmploymentLabel(member)}
        </Tag>
      </Gap>,
    ],
    ['Unit', member.unit],
    ['Role', member.role],
    ['Charge Eligible', getChargeEligibleLabel(member, chargeRole)],
  ];

  return (
    <SideShell.Panel
      header={member.name}
      dataTestId="schedule-worker-profile-panel"
      loading={loading}
      footer={
        <Button kind="primary" onClick={() => onUpdateProfile()}>
          Update Profile
        </Button>
      }
    >
      <Gap vertical size="lg" fullWidth align="stretch">
        <Gap vertical fullWidth align="stretch">
          <Text bold size="lg">
            Profile
          </Text>
          <Gap vertical size="lg" fullWidth align="stretch">
            <AlignedInfo fullWidth content={profileContent} />
            {/* Hours are not yet populated; until then show a placeholder rather than a
                bar fed null values. */}
            {member.fteHours !== null && member.scheduledHours !== null ? (
              <ProgressBar
                value={member.scheduledHours}
                max={member.fteHours}
                color={getFteBarColor(member)}
                title="FTE"
                numbers={`${member.scheduledHours}h of ${member.fteHours}h`}
                description={getFteRemainingLabel(member)}
              />
            ) : (
              <AlignedInfo fullWidth content={[['FTE', '—']]} />
            )}
          </Gap>
        </Gap>

        <Divider secondary />

        <Gap vertical size="md" align="stretch">
          <Text bold size="lg">
            Preferences
          </Text>
          <AlignedInfo
            fullWidth
            content={[
              ['Shift', getShiftTypeLabel(member)],
              ['Shift Pattern', getShiftPatternLabel(member)],
            ]}
          />
        </Gap>

        <Divider secondary />

        <Gap vertical size="md" align="stretch">
          <Text bold size="lg">
            Upcoming Changes
          </Text>
          <ScheduleWorkerUpcomingChanges
            changes={upcomingChanges}
            workerId={member.id}
            schedulingPeriodId={schedulePeriodId}
            onEdit={(changeId) => onUpdateProfile(changeId)}
          />
        </Gap>
      </Gap>
    </SideShell.Panel>
  );
}
